// src/pages/payment/PaymentSuccess.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axiosInstance from '../../utils/axiosInstance';
import { toast } from "react-toastify";
import "./PaymentSuccess.css";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const sessionId = searchParams.get("session_id");
  const courseId = searchParams.get("course_id");

  const [status, setStatus] = useState("processing");
  const [countdown, setCountdown] = useState(10);
  const [enrollmentData, setEnrollmentData] = useState(null);

  useEffect(() => {
    let mounted = true;

    const confirmPayment = async () => {
      try {
        if (!sessionId || !courseId) {
          setStatus("error");
          toast.error("Missing payment/session information.");
          return;
        }

        if (!user) {
          setStatus("error");
          toast.error("Please log in again to confirm your payment.");
          return;
        }

        const res = await axiosInstance.post("/payments/confirm", {
          sessionId,
          courseId,
        });

        if (res?.data?.success) {
          if (!mounted) return;
          setStatus("success");
          setEnrollmentData(res.data.enrollment);
          toast.success(
            "Payment confirmed! Enrollment pending admin approval."
          );
        } else {
          if (!mounted) return;
          setStatus("error");
          toast.error(res?.data?.error || "Payment confirmation failed.");
        }
      } catch (err) {
        if (!mounted) return;
        setStatus("error");
        toast.error(
          err.response?.data?.error ||
            "Error confirming payment. Please contact support."
        );
      }
    };

    confirmPayment();

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [sessionId, courseId, user]);

  // ✅ Handle My Courses navigation
  const handleMyCoursesClick = () => {
    if (user?.role === "admin") {
      navigate("/admin", { replace: true });
    } else {
      navigate("/my-courses", { replace: true });
    }
  };

  // ✅ Force redirect to course list (not a specific course)
  const handleBrowseCoursesClick = () => {
    if (user?.role === "admin") {
      navigate("/admin/courses", { replace: true });
    } else {
      // Full reload to ensure we land on /courses, not /courses/:slug
      sessionStorage.removeItem("selectedCourse");
      navigate("/courses?fromPayment=true", { replace: true });
      setTimeout(() => {
        window.location.assign("/courses?fromPayment=true");
      }, 50);
    }
  };

  return (
    <div className="payment-success-container">
      <div className="payment-status">
        <div className="success-icon">
          {status === "processing" ? "⏳" : status === "success" ? "🎉" : "❌"}
        </div>

        <h1>
          {status === "processing"
            ? "Processing your payment..."
            : status === "success"
            ? "Payment Successful!"
            : "Payment Confirmation Failed"}
        </h1>

        {status === "success" ? (
          <>
            <p>
              Thank you for your purchase! Your payment has been processed
              successfully.
            </p>
            <p>
              Your enrollment is <strong>pending admin approval</strong> and
              should appear in the Admin Dashboard for review.
            </p>
            {enrollmentData && (
              <div className="payment-info">
                <p>
                  <strong>Enrollment ID:</strong> {enrollmentData.id}
                </p>
                <p>
                  <strong>Status:</strong> {enrollmentData.approval_status}
                </p>
              </div>
            )}
          </>
        ) : status === "processing" ? (
          <p>Please wait while we confirm your enrollment...</p>
        ) : (
          <p>
            We couldn't confirm your payment. Please contact support with your
            Reference ID below.
          </p>
        )}

        <hr />

        <div className="processing-message">
          <h3>What happens next?</h3>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <strong>Payment Processed</strong>
                <p>Your payment has been securely processed by Stripe.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <strong>Enrollment Created</strong>
                <p>
                  If payment succeeded, an enrollment record is created and set
                  to <em>pending</em> for admin approval.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <strong>Admin Approval</strong>
                <p>
                  An admin will review and approve the enrollment. Once approved,
                  you'll get full access to the course.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="important-notes">
          <h4>📝 Important Notes</h4>
          <ul>
            <li>
              Enrollment is processed automatically via Stripe webhooks and a
              backend confirmation endpoint.
            </li>
            <li>
              If you do not see your course listed after a few minutes, contact
              support and provide the Reference ID below.
            </li>
            <li>Admin approval typically takes 1–2 business hours.</li>
          </ul>
        </div>

        <div className="support-info">
          <p>
            <strong>Reference ID:</strong> {sessionId || "N/A"}
          </p>
          <p>
            <strong>Course ID:</strong> {courseId || "N/A"}
          </p>
          {enrollmentData && (
            <p>
              <strong>Enrollment ID:</strong> {enrollmentData.id}
            </p>
          )}
        </div>

        <div className="action-buttons">
          <button className="btn-primary" onClick={handleMyCoursesClick}>
            {countdown > 0
              ? `Go to My Courses (${countdown})`
              : "Go to My Courses"}
          </button>

          <button className="btn-secondary" onClick={handleBrowseCoursesClick}>
            Browse More Courses
          </button>

          <a href="mailto:support@matheclass.com" className="btn-support">
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
